import { useState, useEffect, useCallback } from 'react';

import { ArrowRight } from 'lucide-react';

import { SalesOrderPayload, BillingCycle } from './types';
import { JsonTreeEditor } from './JsonTreeEditorV4';
import { SalesOrderStatusPills } from './SalesOrderStatusPills';
import { SalesOrderStatusEnum, SalesOrderSourcePascalCaseEnum } from '@/graphql/contracts/_generated_/contracts';
import Spinner from '@/components/common/Spinner/Spinner';

interface SalesOrderPayloadEditorProps {
  onSave: (payload: SalesOrderPayload) => void;
  onOriginalPayloadLoad?: (payload: SalesOrderPayload) => void;
  currentBillingCycleContractLineId?: string;
  cycle?: BillingCycle;
  salesOrderStatus?: SalesOrderStatusEnum | null;
  salesOrderSource?: SalesOrderSourcePascalCaseEnum | null;
  initialPayload?: SalesOrderPayload | null;
  isLoading?: boolean;
  /** 
   * Controls whether the "Original Sales Order" section with status badges is shown.
   * Set to false when the cycle is Excluded or when viewing a sales order directly.
   * Defaults to true.
   */
  shouldShowOriginalPayloadBadges?: boolean;
  /**
   * Callback when validation state changes.
   * Called with isValid=true when there are no validation errors,
   * and isValid=false with error messages when validation fails.
   */
  onValidationChange?: (isValid: boolean, errors: string[]) => void;
  /** Bulk billing start date (YYYY-MM-DD) to apply to all contract lines */
  bulkBillingStartDate?: string;
  /** Bulk billing end date (YYYY-MM-DD) to apply to all contract lines */
  bulkBillingEndDate?: string;
  /** Formatted original total string (e.g. "GBP 2.73") */
  formattedOriginalTotal?: string;
  /** Formatted current total string (e.g. "GBP 2.73") */
  formattedCurrentTotal?: string;
  /** Whether totals have changed */
  totalsChanged?: boolean;
  /** Whether current total is greater than original */
  currentTotalHigher?: boolean;
  /** Excluded cycle start date (YYYY-MM-DD) for date coverage validation */
  excludedCycleStartDate?: string;
  /** Excluded cycle end date (YYYY-MM-DD) for date coverage validation */
  excludedCycleEndDate?: string;
}

export function SalesOrderPayloadEditor({ 
  onSave,
  onOriginalPayloadLoad,
  currentBillingCycleContractLineId,
  cycle,
  salesOrderSource,
  salesOrderStatus,
  initialPayload,
  isLoading = false,
  shouldShowOriginalPayloadBadges = true,
  onValidationChange,
  bulkBillingStartDate,
  bulkBillingEndDate,
  formattedOriginalTotal,
  formattedCurrentTotal,
  totalsChanged,
  currentTotalHigher,
  excludedCycleStartDate,
  excludedCycleEndDate,
}: SalesOrderPayloadEditorProps) {
  const [payload, setPayload] = useState<SalesOrderPayload | null>(null);
  const [originalPayload, setOriginalPayload] = useState<SalesOrderPayload | null>(null);
  const [removedLineIds, setRemovedLineIds] = useState<string[]>([]);

  // Handler for when removed lines change in JsonTreeEditor
  const handleRemovedLinesChange = useCallback((lineIds: string[]) => {
    setRemovedLineIds(lineIds);
  }, []);

  // Initialize payload from API response when it becomes available
  useEffect(() => {
    if (initialPayload) {
      const payloadCopy = JSON.parse(JSON.stringify(initialPayload));
      setPayload(payloadCopy);
      setOriginalPayload(JSON.parse(JSON.stringify(initialPayload)));
    }
  }, [initialPayload]);

  const isOneEvo = payload?.InvoiceMethod === 'OneEvo';

  // Send original payload to parent when it changes
  useEffect(() => {
    if (onOriginalPayloadLoad && originalPayload) {
      onOriginalPayloadLoad(originalPayload);
    }
  }, [onOriginalPayloadLoad, originalPayload]);

  // Update parent with effective payload (filtered by removed lines) whenever payload or removedLineIds change
  useEffect(() => {
    if (payload) {
      // Create effective payload by filtering out removed contract lines
      const effectivePayload: SalesOrderPayload = {
        ...payload,
        ContractLines: payload.ContractLines
          ? payload.ContractLines.filter(
            (line: any) => !removedLineIds.includes(line.ContractLineID)
          )
          : [],
      };
      onSave(effectivePayload);
    }
  }, [payload, removedLineIds, onSave]);

  // Count modifications
  const countModifications = (): number => {
    if (!payload || !originalPayload) return 0;
    
    let count = 0;
    
    // Check header fields
    Object.keys(payload).forEach(key => {
      if (key !== 'Bundles' && key !== 'ContractLines') {
        if (JSON.stringify((payload as any)[key]) !== JSON.stringify((originalPayload as any)[key])) {
          count++;
        }
      }
    });
    
    // Check contract lines
    if (payload.ContractLines && Array.isArray(payload.ContractLines)) {
      payload.ContractLines.forEach((line, index) => {
        const origLine = originalPayload.ContractLines?.[index];
        if (origLine) {
          Object.keys(line).forEach(key => {
            const lineVal = JSON.stringify((line as Record<string, unknown>)[key]);
            const origVal = JSON.stringify((origLine as Record<string, unknown>)[key]);
            if (lineVal !== origVal) {
              count++;
            }
          });
        }
      });
    }
    
    return count;
  };

  const totalModifications = countModifications();

  // Detect if any contract line has ProRataType 'ManualInvoicing' (from API or triggered by date changes)
  const hasProRataManualInvoicing = (() => {
    if (!payload?.ContractLines) return false;
    return payload.ContractLines.some((line: Record<string, unknown>) => {
      return line.ProRataType === 'ManualInvoicing';
    });
  })();

  // Show loading state with spinning animation
  if (isLoading) {
    return (
      <div className="flex flex-col h-[calc(95vh-230px)] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Spinner />
          <div className="text-sm text-muted-foreground">Loading sales order payload...</div>
        </div>
      </div>
    );
  }

  // Show empty state if payload or originalPayload is missing
  if (!payload || !originalPayload) {
    return (
      <div className="flex flex-col h-[calc(95vh-230px)] items-center justify-center">
        <div className="text-sm text-muted-foreground">No payload data available</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(95vh-230px)]">
      {/* Compact Header */}
      <div className="flex flex-col gap-1.5 mb-2 pb-2 border-b flex-shrink-0">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-900">Sales Order Payload</h3>
          {totalModifications > 0 && (
            <div className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
              {totalModifications} change{totalModifications !== 1 ? 's' : ''}
            </div>
          )}
          {hasProRataManualInvoicing && (
            <div className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded">
              ProRata Manual Invoicing
            </div>
          )}
        </div>
        
        {/* Status Pills + Totals inline */}
        <div className="flex items-center gap-2">
          {cycle && shouldShowOriginalPayloadBadges && (
            <>
              <span className="text-sm text-muted-foreground">Original Sales Order:</span>
              <SalesOrderStatusPills 
                source={salesOrderSource} 
                status={salesOrderStatus} 
              />
            </>
          )}

          {formattedOriginalTotal && formattedCurrentTotal && (
            <>
              {cycle && shouldShowOriginalPayloadBadges && (
                <span className="text-muted-foreground">|</span>
              )}
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-muted-foreground">Original Total:</span>
                <span className="text-sm font-semibold text-foreground">
                  {formattedOriginalTotal}
                </span>
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={2.5} />
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-muted-foreground">Current Total:</span>
                <span className={`text-sm font-semibold ${
                  totalsChanged
                    ? currentTotalHigher
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-orange-600 dark:text-orange-400'
                    : 'text-foreground'
                }`}>
                  {formattedCurrentTotal}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* JSON Tree Editor - No parent scrolling */}
      <div className="flex-1 min-h-0">
        <JsonTreeEditor
          payload={payload}
          originalPayload={originalPayload}
          onPayloadChange={setPayload}
          currentBillingCycleContractLineId={currentBillingCycleContractLineId}
          invoiceMethod={isOneEvo ? 'OneEvo' : 'Legacy'}
          onValidationChange={onValidationChange}
          onRemovedLinesChange={handleRemovedLinesChange}
          bulkBillingStartDate={bulkBillingStartDate}
          bulkBillingEndDate={bulkBillingEndDate}
          excludedCycleStartDate={excludedCycleStartDate}
          excludedCycleEndDate={excludedCycleEndDate}
        />
      </div>
    </div>
  );
}
